import { Building2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Callout, EmptyState, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import {
  MonoCell, Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Page, PageHeader } from "@/components/layout/page";
import { useTenants } from "@/lib/queries";
import { formatDateTime } from "@/lib/utils";

/**
 * Tenants. Superadmin-only, and there is one.
 *
 * AI Case Leads is the tenant; the law firms it calls for are campaigns, not tenants — a setter
 * works several firms' leads from one account, which a tenant-per-firm model could not express.
 * The screen exists because the scoping is real in the schema and someone will eventually need
 * to see it.
 */
export function TenantsPage() {
  const { data, isPending, error, refetch } = useTenants();

  return (
    <Page>
      <PageHeader
        title="Tenants"
        description="The account boundary. Every user except a superadmin belongs to exactly one."
      />

      <Callout tone="info" className="mb-4">
        Law firms are campaigns, not tenants — reps work several firms' leads from one account.
        A second tenant would be a separate organisation using this platform.
      </Callout>

      <Card className="overflow-hidden">
        {isPending ? (
          <TableSkeleton cols={4} />
        ) : error ? (
          <ErrorState
            error={error}
            title="Could not load tenants"
            action={
              <Button variant="outline" onClick={() => void refetch()}>
                Try again
              </Button>
            }
          />
        ) : data.length === 0 ? (
          <EmptyState
            icon={Building2}
            title="No tenants"
            description="Run `make seed-identity` in the platform repo to create the first tenant and superadmin."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Users</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((t) => (
                <TableRow key={t.id}>
                  <TableCell className="font-medium">{t.name}</TableCell>
                  <MonoCell className="text-text-muted">{t.slug}</MonoCell>
                  <TableCell>{t.user_count}</TableCell>
                  <TableCell>
                    <Badge tone={t.status === "active" ? "success" : "danger"}>
                      {t.status === "active" ? "Active" : "Suspended"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-text-muted">
                    {formatDateTime(t.created_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </Page>
  );
}
